import { useEffect, useState } from 'react'
import { adminAPI } from '../../lib/api'
import type { AdminSummary, User } from '../../types'
import { Button } from '../../components/ui/Button'
import { getErrorMessage } from '../../lib/utils'
import {
  Users, Building2, Calendar, DollarSign,
  TrendingUp, TrendingDown, Activity, Download,
  Shield, AlertCircle, CheckCircle, Clock
} from 'lucide-react'

export function AdminDashboardPage() {
  const [user, setUser] = useState<User | null>(null)
  const [summary, setSummary] = useState<AdminSummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        // Fetch admin session
        const sessionRes = await fetch('/api/admin/session', { credentials: 'include' })
        if (sessionRes.ok) {
          const sessionData = await sessionRes.json()
          if (mounted && sessionData.success && sessionData.data?.user) {
            setUser(sessionData.data.user)
          }
        }

        // Fetch summary
        const res = await adminAPI.summary()
        if (mounted && res.success) {
          setSummary(res.data as AdminSummary)
        } else if (mounted && !res.success) {
          setError(res.message || 'Failed to load summary')
        }
      } catch (e: unknown) {
        if (mounted) setError(getErrorMessage(e, 'Failed to load'))
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => { mounted = false }
  }, [])

  const StatCard = ({
    title,
    value,
    icon: Icon,
    trend,
    trendValue,
    color = 'orange'
  }: {
    title: string
    value: string | number
    icon: any
    trend?: 'up' | 'down'
    trendValue?: string
    color?: 'orange' | 'blue' | 'green' | 'purple'
  }) => {
    const colorClasses = {
      orange: 'from-orange-500 to-red-500',
      blue: 'from-blue-500 to-indigo-500',
      green: 'from-green-500 to-emerald-500',
      purple: 'from-purple-500 to-pink-500'
    }

    return (
      <div className="card hover:shadow-lg transition-all duration-300 group">
        <div className="card-body">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
              <p className="text-3xl font-bold mt-2 text-gray-900 dark:text-gray-100">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></span>
                ) : (
                  value
                )}
              </p>
              {trendValue && (
                <div className={`flex items-center gap-1 mt-2 text-sm ${
                  trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {trend === 'up' ? (
                    <TrendingUp className="h-4 w-4" />
                  ) : (
                    <TrendingDown className="h-4 w-4" />
                  )}
                  <span className="font-medium">{trendValue}</span>
                </div>
              )}
            </div>
            <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} group-hover:scale-110 transition-transform`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Global Admin Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2 flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Platform-wide monitoring and management
          </p>
        </div>
        {user && (
          <div className="flex items-center gap-3 px-4 py-2 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 rounded-xl border border-orange-200 dark:border-orange-800">
            <Shield className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            <div className="text-sm">
              <div className="font-semibold text-gray-900 dark:text-gray-100">{user.name}</div>
              <div className="text-xs text-orange-600 dark:text-orange-400">{user.role}</div>
            </div>
          </div>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-800 rounded-xl">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-red-900 dark:text-red-100">Error loading data</h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-orange-600" />
          Key Metrics
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Tenants"
            value={summary?.tenants ?? '—'}
            icon={Building2}
            color="blue"
            trend="up"
            trendValue="+12% this month"
          />
          <StatCard
            title="Active Tenants"
            value={summary?.active_tenants ?? '—'}
            icon={CheckCircle}
            color="green"
            trend="up"
            trendValue="95% active rate"
          />
          <StatCard
            title="Total Users"
            value={summary?.users ?? '—'}
            icon={Users}
            color="purple"
            trend="up"
            trendValue="+8% this week"
          />
          <StatCard
            title="Total Bookings"
            value={summary?.bookings ?? '—'}
            icon={Calendar}
            color="orange"
            trend="up"
            trendValue="+24% this month"
          />
        </div>
      </div>

      {/* Revenue Card */}
      <div className="card bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/5 dark:to-red-500/5 border-orange-200 dark:border-orange-800">
        <div className="card-body">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Total Platform Revenue
              </p>
              <p className="text-4xl font-bold mt-2 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                {loading ? (
                  <span className="inline-block w-32 h-10 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></span>
                ) : (
                  `£${((summary?.revenue ?? 0) / 100).toFixed(2)}`
                )}
              </p>
              <div className="flex items-center gap-1 mt-2 text-sm text-green-600 dark:text-green-400">
                <TrendingUp className="h-4 w-4" />
                <span className="font-medium">+18.2% from last month</span>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 shadow-lg">
              <DollarSign className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Export Reports */}
        <div className="card">
          <div className="card-header flex items-center gap-2">
            <Download className="h-5 w-5 text-orange-600" />
            <h3 className="font-semibold">Export Reports</h3>
          </div>
          <div className="card-body space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Download platform-wide data exports for analysis and reporting
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={async () => {
                  const blob = await adminAPI.downloadBookingsCsv()
                  const link = document.createElement('a')
                  link.href = URL.createObjectURL(blob)
                  link.download = `platform-bookings-${new Date().toISOString().split('T')[0]}.csv`
                  link.click()
                }}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Bookings Data
              </Button>
              <Button
                onClick={async () => {
                  const blob = await adminAPI.downloadRevenueCsv()
                  const link = document.createElement('a')
                  link.href = URL.createObjectURL(blob)
                  link.download = `platform-revenue-${new Date().toISOString().split('T')[0]}.csv`
                  link.click()
                }}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Revenue Report
              </Button>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="card">
          <div className="card-header flex items-center gap-2">
            <Activity className="h-5 w-5 text-green-600" />
            <h3 className="font-semibold">System Status</h3>
          </div>
          <div className="card-body space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-500/10 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">API Services</span>
              </div>
              <span className="text-sm font-semibold text-green-600 dark:text-green-400">Operational</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-500/10 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Database</span>
              </div>
              <span className="text-sm font-semibold text-green-600 dark:text-green-400">Healthy</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-500/10 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Worker Runtime</span>
              </div>
              <span className="text-sm font-semibold text-green-600 dark:text-green-400">Online</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="card-header flex items-center gap-2">
          <Clock className="h-5 w-5 text-orange-600" />
          <h3 className="font-semibold">Recent Platform Activity</h3>
        </div>
        <div className="card-body">
          <div className="space-y-3">
            {loading ? (
              <>
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg animate-pulse">
                    <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Activity feed coming soon</p>
                <p className="text-sm mt-1">Track tenant actions, user signups, and system events</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
